import { Search, SearchDbRecordResult, SearchType } from '@/types';
import { SearchMetadata } from '@/types/shared';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import { usePrefetchQuery, useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { logger, parseJSONWithoutException } from '@/util';
import callBackendApi, { useCallBackendApi } from './call';

export const createSearch = async (
  token: string,
  values: {
    name: string;
    savedBySub: string;
    assignedToSubs: string[];
    description: string;
    searchJson: string;
    searchType: SearchType;
  }
) => {
  logger.debug('createSearch() called', values);

  const data: SearchDbRecordResult[] = await callBackendApi({
    token,
    method: 'post',
    endpoint: '/searches',
    data: values,
  });
  return data;
};

export const getSearchesBySavedBySub = async (token: string, savedBySub: string) => {
  logger.debug('getSearchesBySavedBySub() called');

  const data: SearchDbRecordResult[] = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/searches/getbysavedbysub/${encodeURIComponent(savedBySub)}`,
  });

  return data;
};

export const getSearchesByAssignedToSub = async (token: string, assignedToSub: string) => {
  logger.debug('getSearchesByAssignedToSub() called');

  const data: SearchDbRecordResult[] = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/searches/getbyassignedtosub/${encodeURIComponent(assignedToSub)}`,
  });

  return data;
};

// Returns true if a duplicate encountered; false otherwise.

export const checkSearches = async (
  token: string,
  values: { name: string; assignedToSubs: string[] }
) => {
  logger.debug('checkSearches() called', {});

  const { name, assignedToSubs } = values;

  const data = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/searches/check?name=${encodeURIComponent(name)}&assignedToSubs=${assignedToSubs
      .map((x) => encodeURIComponent(x))
      .join(',')}`,
  });
  return data;
};

export const deleteSearch = async (token: string, searchId: number) => {
  logger.debug('deleteSearch() called', {});

  const data = await callBackendApi({ token, method: 'delete', endpoint: `/searches/${searchId}` });
  return data;
};

export const updateSearch = async (
  token: string,
  id: number,
  values: Partial<Search> & Pick<Search, 'name'>
) => {
  logger.debug('updateSearch() called', {});

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: `/searches/${id}`,
    data: values,
  });
  return data;
};

type AdvancedSearch = {
  type: 'Advanced';
  data: SearchTreeNode | undefined;
  metadata: SearchMetadata;
};

type QuickSearch = {
  type: 'Quick';
  data: unknown;
  metadata: SearchMetadata;
};

export type LoadedSearch = QuickSearch | AdvancedSearch;

function useLoadSearch(searchId: number) {
  const getSearchById = useCallBackendApi<null, SearchDbRecordResult>();
  const location = useLocation();

  const isValidSearch = !isNaN(searchId) && searchId > 0;

  const locationSearchType = location.pathname === '/advancedsearch' ? 'Advanced' : 'Quick';

  return async (): Promise<LoadedSearch> => {
    if (!isValidSearch) {
      return {
        type: locationSearchType,
        data: undefined,
        metadata: {
          description: undefined,
          name: undefined,
          id: undefined,
        },
      };
    }

    logger.debug('getSearchById() called');

    const search = await getSearchById({ endpoint: `/searches/${searchId}`, method: 'get' });

    return {
      type: search.searchType,
      data: parseJSONWithoutException(search?.searchJson ?? ''),
      metadata: {
        name: search.name,
        description: search.description,
        id: search.id,
      },
    };
  };
}

export function usePrefetchSearch(searchId: number) {
  const loadSearch = useLoadSearch(searchId);
  return usePrefetchQuery({ queryKey: ['search', searchId], queryFn: loadSearch });
}

export const useSearch = (searchId: number) => {
  const loadSearch = useLoadSearch(searchId);
  return useQuery({
    queryKey: ['search', searchId],
    queryFn: loadSearch,
    refetchOnWindowFocus: false,
  });
};
